<template>
  <div class="user-row" :class="{ active, switching }" @click="onRowClick">
    <div class="avatar" :style="`background-image: url('${skinUrl}')`"></div>
    <div class="user-row__id">
      <h4>{{ name }}</h4>
      <p>{{ description }}</p>
    </div>

    <span v-if="switching" class="switching-tag">
      <span class="switching-dot"></span>
      Connexion en cours…
    </span>

    <span v-else-if="active" class="active-tag">
      <svg viewBox="0 0 12 12" aria-hidden="true"><path d="M2.5 6.4 4.9 8.8 9.6 3.6" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" /></svg>
      Actif
    </span>

    <div v-if="!switching" class="menu" @click.stop>
      <button type="button" class="menu-btn" aria-label="Options du compte" @click="toggleMenu">
        <svg viewBox="0 0 4 16" aria-hidden="true">
          <circle cx="2" cy="2" r="1.6" /><circle cx="2" cy="8" r="1.6" /><circle cx="2" cy="14" r="1.6" />
        </svg>
      </button>
      <div v-if="menuOpen" class="popover">
        <template v-if="!confirming">
          <button v-if="!active" type="button" class="popover-item" @click="useAccount">Utiliser ce compte</button>
          <button type="button" class="popover-item popover-item--danger" @click="confirming = true">Se déconnecter</button>
        </template>
        <template v-else>
          <p class="popover-confirm">Déconnecter <b>{{ name }}</b> ?</p>
          <div class="popover-actions">
            <button type="button" class="popover-cancel" @click="confirming = false">Annuler</button>
            <button type="button" class="popover-danger" @click="logoutUser">Déconnecter</button>
          </div>
        </template>
      </div>
    </div>
  </div>
</template>

<script>
import { requestAction } from "@/utils/cef";

export default {
  name: 'UserCard',
  props: {
    name: String,
    skinUrl: String,
    description: String,
    active: Boolean,
    mcuuid: String,
  },
  data() {
    return {
      menuOpen: false,
      confirming: false,
      switching: false,
    };
  },
  mounted() {
    window.addEventListener('click', this.closeMenu);
  },
  beforeUnmount() {
    window.removeEventListener('click', this.closeMenu);
  },
  methods: {
    onRowClick() {
      if (this.active || this.switching) {
        return;
      }
      this.changeAccount();
    },
    toggleMenu() {
      this.menuOpen = !this.menuOpen;
      this.confirming = false;
    },
    closeMenu() {
      this.menuOpen = false;
      this.confirming = false;
    },
    useAccount() {
      this.menuOpen = false;
      this.changeAccount();
    },
    changeAccount() {
      if (!this.mcuuid || this.switching) {
        return;
      }
      this.switching = true;
      this.menuOpen = false;
      requestAction("SWITCH_ACCOUNT", { uuid: this.mcuuid }, (r) => {
        if (r.status === 200) {
          this.rebootToSplash();
        } else {
          this.switching = false;
        }
      });
    },
    logoutUser() {
      if (!this.mcuuid || this.switching) {
        return;
      }
      this.switching = true;
      requestAction("LOGOUT_ACCOUNT", { uuid: this.mcuuid }, (r) => {
        if (r.status === 200) {
          this.rebootToSplash();
        } else {
          this.switching = false;
        }
      });
    },
    rebootToSplash() {
      this.$store.commit("setGames", null);
      window.location.hash = "#/splash";
      window.location.reload();
    },
  },
};
</script>

<style lang="scss" scoped>
.user-row {
  height: 64px;
  padding: 0 16px;
  display: flex;
  align-items: center;
  gap: 14px;
  cursor: pointer;
  transition: background-color 0.15s ease;

  & + .user-row {
    border-top: 1px solid #212121;
  }

  &:hover {
    background-color: #1c1c1c;
  }

  &.active {
    cursor: default;
  }

  .avatar {
    flex: none;
    width: 40px;
    height: 40px;
    border-radius: 6px;
    background-size: cover;
    background-position: center;
    image-rendering: pixelated;
    -ms-interpolation-mode: nearest-neighbor;
  }

  .user-row__id {
    flex: 1 1 auto;
    min-width: 0;

    h4 {
      font-size: 15px;
      font-weight: 600;
      color: #ffffff;
    }

    p {
      margin-top: 2px;
      font-size: 12.5px;
      color: #8f8f99;
    }
  }

  &.switching {
    cursor: default;

    .avatar,
    .user-row__id {
      opacity: 0.55;
    }
  }

  .switching-tag {
    flex: none;
    display: inline-flex;
    align-items: center;
    gap: 8px;
    font-size: 12px;
    font-weight: 600;
    color: #ff5c00;

    .switching-dot {
      width: 8px;
      height: 8px;
      border-radius: 50%;
      background: #ff5c00;
    }
  }

  .active-tag {
    flex: none;
    display: inline-flex;
    align-items: center;
    gap: 5px;
    font-size: 11px;
    font-weight: 700;
    letter-spacing: 0.04em;
    text-transform: uppercase;
    color: #ff5c00;

    svg {
      width: 11px;
      height: 11px;
    }
  }

  .menu {
    flex: none;
    position: relative;

    .menu-btn {
      width: 28px;
      height: 28px;
      display: flex;
      align-items: center;
      justify-content: center;
      background: transparent;
      border: 0;
      border-radius: 6px;
      color: #5c5c68;
      cursor: pointer;

      svg {
        width: 4px;
        height: 16px;
        fill: currentColor;
      }

      &:hover {
        color: #b4b4bc;
        background: rgba(255, 255, 255, 0.06);
      }
    }

    .popover {
      position: absolute;
      top: calc(100% + 6px);
      right: 0;
      z-index: 20;
      width: 200px;
      padding: 6px;
      background: #1c1c22;
      border: 1px solid #2a2a2a;
      border-radius: 8px;
      box-shadow: 0 12px 30px -12px rgba(0, 0, 0, 0.7);
      cursor: default;

      .popover-item {
        display: block;
        width: 100%;
        text-align: left;
        height: 34px;
        padding: 0 10px;
        background: transparent;
        border: 0;
        border-radius: 5px;
        color: #d8d8de;
        font-size: 13px;
        font-weight: 500;
        cursor: pointer;

        &:hover {
          background: rgba(255, 255, 255, 0.06);
        }

        &--danger {
          color: #ff453a;
        }
      }

      .popover-confirm {
        padding: 6px 8px 10px;
        font-size: 13px;
        color: #d8d8de;

        b {
          color: #ffffff;
        }
      }

      .popover-actions {
        display: flex;
        gap: 6px;

        button {
          flex: 1 1 0;
          height: 30px;
          border-radius: 5px;
          font-size: 12.5px;
          font-weight: 600;
          cursor: pointer;
        }

        .popover-cancel {
          background: transparent;
          border: 1px solid #2a2a2a;
          color: #b4b4bc;

          &:hover {
            color: #ffffff;
          }
        }

        .popover-danger {
          background: #ff453a;
          border: 0;
          color: #ffffff;

          &:hover {
            background: #e23c32;
          }
        }
      }
    }
  }
}
</style>
